import { useMember } from 'components/Providers'
import { Box, Chip, Grid, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { getRaceTypes } from 'store/lookup'
import { useEffect, useState } from 'react'
import PreviewCard, { AssignedAssets } from 'components/PreviewCard'
import FeildSkeleton from '../Contact/List/FieldSkeleton'
import { useTranslation } from 'react-i18next'
import { useBasePath } from 'components/Providers'
import NotFound from 'components/NotFound'
import 'utils/global.scss'
import { getNurseConditionTypes } from 'store/lookup'
import { capitalize } from 'lodash'
import { LockOpen, DirectionsWalk as DirectionsWalkIcon, DirectionsCar as DirectionsCarIcon, Medication as MedicationIcon, DirectionsBusFilled } from '@mui/icons-material'


const genders = ['Male', 'Female', 'Non-Binary/Undesignated']

const ethnicities = ['Hispanic or Latino', 'Not Hispanic or Latino']

export default function MemberProfile() {
  const member = useMember()
  const dispatch = useDispatch()
  const memberId = member?.id
  const employmentType = member?.person?.type
  const theme = useSelector(state => state.session.appearance)
  const raceTypes = useSelector(state => state.lookup.raceTypes)
  const gradeLevels = useSelector(state => state?.lookup?.gradeLevels)
  const [notFound, setNotFound] = useState(false)
  const { t } = useTranslation()
  const basePath = useBasePath()
  const healthConditions = member?.person?.health
  const types = useSelector(state => state.lookup.nurseConditionType)
  const restrain = member?.person?.restrainingOrder
  const isWalker = member?.person?.isWalker
  const lockerInfo = member?.person?.miscellaneous?.lockerInfo
  const busInfo = member?.person?.miscellaneous?.busInfo
  const assignedAssets = member?.person?.devices

  useEffect(() => {
    const fetchData = async () => {
      const [raceTypesRes, gradeLevelsRes, memberContactsRes] = await Promise.all([
        !raceTypes && dispatch(getRaceTypes()),
      ])
      if (raceTypesRes?.error || gradeLevelsRes?.error || memberContactsRes?.error) {
        setNotFound(true)
      }
    }

    fetchData()
  }, [dispatch, memberId, member?.contacts, employmentType])

  useEffect(() => {
    types || dispatch(getNurseConditionTypes())
  }, [dispatch, Boolean (types)])

  const isReadyToGo = member && raceTypes && gradeLevels

  const sped = member.stateData?.sped
  const ell = member.stateData?.ell?.status
  const section504 = member.stateData?.section504
  const health = member.stateData?.healthRelatedInfo?.healthInsuranceStatus

  if (!isReadyToGo && !notFound) return null

  let races = ''
  if (Array.isArray(member.person?.raceTypeIds)) {
    member.person?.raceTypeIds.forEach(item => {
      const race = raceTypes?.find(rt => rt.id === item)
      if (race) {
        races = `${races} ${race.name},`
      }
    })
  }

  const healthConditionTooltip = () => {
    return (
      <Tooltip
      title={
        <Box>
          <Typography variant='body2' sx={{ fontWeight: 600, textAlign: 'center' }}>{t('Health Conditions')}</Typography>
                <Table size="small">
                  <TableHead >
                    <TableRow>
                      <TableCell sx={{ color: 'primary.contrastText' }}>{t('Condition')}</TableCell>
                      <TableCell sx={{color: 'primary.contrastText' }} >{t('Explanation')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {healthConditions
                    .map((condition, index) => (
                    <TableRow hover key={index} >
                      <TableCell sx={{color: 'primary.contrastText' }} >{types?.find(type => type.id === condition.conditionTypeId)?.name}</TableCell>
                      <TableCell sx={{color: 'primary.contrastText' }} >{condition.explanation}</TableCell>
                    </TableRow>
                ))}
                  </TableBody>
                </Table>
        </Box>
      }
    >
     <MedicationIcon color='primary' fontSize='medium' sx={{ mr: 1 }} />
    </Tooltip>
    )
  }


  let dismissal = null
  if (isWalker !== null && isWalker !== undefined) {
    dismissal = isWalker
      ? { Icon: DirectionsWalkIcon, label: t('Walker'), detail: t('Walks home at dismissal.') }
      : { Icon: DirectionsCarIcon, label: t('Pick-up'), detail: t('Picked up at dismissal by an authorized contact.') }
  }

  const profileData = [
    {
      key: 'Full Name',
      value: `${member?.person?.firstName} ${member?.person?.lastName}` || '',
    },
    { key: 'State ID', value: member?.person?.stateId || '' },
    {
      key: 'Date of Birth',
      value: member?.person?.dateOfBirth ? dayjs(member?.person?.dateOfBirth).format('MM-DD-YYYY') : '',
    },
    { key: 'Email/Username', value: member.person?.email || '' },

    {
      key: 'Gender',
      value: member?.person?.gender ? genders.find(g => g.toLowerCase() === member?.person?.gender.toLowerCase()) : '',
    },
    {
      key: 'Ethnicity',
      value: member?.person?.ethnicity
        ? ethnicities.find(e => e.toLowerCase() === member?.person?.ethnicity.replaceAll(/-/g, ' ').toLowerCase())
        : '',
    },
    { key: 'Race', value: races || '' },
    {
      key: 'Address',
      value: `${member.person?.addresses?.[0]?.street || ''} ${member.person?.addresses?.[0]?.city || ''} ${
        member.person?.addresses?.[0]?.state || ''
      } ${member.person?.addresses?.[0]?.zip || ''}`,
    },
  ]


  const staffProfileData = [
    { key: 'Full Name', value: `${member?.person?.firstName} ${member?.person?.middleName ?? ''} ${member?.person?.lastName}` || '', },
    { key: 'State ID', value: member?.person?.stateId || '' },
    { key: 'Gender', value: member?.person?.gender ? genders.find(g => g.toLowerCase() === member?.person?.gender.toLowerCase()) : '',},
    { key: 'School Email', value: member.person?.email || '' },
    { key: 'Personal Email', value: member.person?.email || '' },
    { key: 'School Extension', value: member.person?.phones?.find(phone => phone.type === 'work')?.number || ' ' },
    { key: 'Home Phone', value: member.person?.phones?.find(phone => phone.type === 'home')?.number || ' ' },
    { key: 'Cell Phone', value: member.person?.phones?.find(phone => phone.type === 'mobile')?.number || ' ' },
    { key: 'Address',
      value: `${member.person?.addresses?.[0]?.street || ''} ${member.person?.addresses?.[0]?.city || ''} ${
        member.person?.addresses?.[0]?.state || ''
      } ${member.person?.addresses?.[0]?.zip || ''}`,},
  ]

  // if (!(raceTypes && gradeLevels)) return <LinearProgress />

  if (notFound) return <NotFound basePath={`${basePath}`} />

  return (
    <Grid container>
      <Grid item xs={12} md={12} sx={{ height: '100%', mx: 3, mt: 3 }}>
        <PreviewCard
          title={t('Personal Information')}
          size="large"
          buttonNavigate={`/member/${member.person?.type}/${member.id}/person`}
          buttonLabel={t('More')}
          buttonPermission={member.personType === 'student' ? 'setting.permission' : 'setting.permission'}
          actionChildren={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {sped && <Chip label="SPED" sx={{ mr: 1 }} />}
                {section504 && <Chip label="504" sx={{ mr: 1 }} />}
                {ell && <Chip label="ELL" sx={{ mr: 1 }} />}
                {health && <Chip label="Health" sx={{ mr: 1 }} />}
                {restrain && (
                  <Tooltip
                    title={
                      restrain
                        ? `${restrain.guardianName} — ${capitalize(restrain.relation)} is not allowed to pick up the student.`
                        : t('Restraining Order')
                    }
                  >
                    <Chip label="RO" sx={{ mr: 1 }} />
                  </Tooltip>
                )}
                {healthConditions?.length > 0 && healthConditionTooltip()}
                {dismissal && (
                  <Tooltip title={`${dismissal.label} — ${dismissal.detail}`}>
                    <dismissal.Icon color="primary" fontSize="medium" sx={{ mr: 1 }} />
                  </Tooltip>
                )}
                {lockerInfo && (
                  <Tooltip title={`Locker Info: ${lockerInfo}`}>
                    <LockOpen color="primary" fontSize="medium" sx={{ mr: 1 }} />
                  </Tooltip>
                )}
                {busInfo && (
                  <Tooltip title={`Bus: ${busInfo}`}>
                    <DirectionsBusFilled color="primary" fontSize="medium" sx={{ mr: 1 }} />
                  </Tooltip>
                )}
                {assignedAssets?.length > 0 && <AssignedAssets assets={assignedAssets} />}
              </Box>
          }
        >
          <Grid item xs={12} md={12}>
            <Grid container spacing={2}>
              {employmentType === "student" ? (
                profileData.map((item, index) => (
                <Grid key={index} item sm={4} xs={4} md={4}>
                  <Typography className={`grid-table-title-${theme}`} variant="body2" sx={{ fontSize: '12px' }}>
                    {t(item.key)}
                  </Typography>
                  <Typography variant="body1" className={`grid-table-title-value`}>
                    {item.value ? item.value : <FeildSkeleton />} &nbsp;
                  </Typography>
                </Grid>
              ))
            ) : (
              staffProfileData.map((item, index) => (
                <Grid key={index} item sm={4} xs={4} md={4}>
                  <Typography className={`grid-table-title-${theme}`} variant="body2" sx={{ fontSize: '12px' }}>
                    {t(item.key)}
                  </Typography>
                  <Typography variant="body1" className={`grid-table-title-value`}>
                    {item.value ? item.value : <FeildSkeleton />} &nbsp;
                  </Typography>
                </Grid>
              ))
            )}
            </Grid>
          </Grid>
        </PreviewCard>
      </Grid>
    </Grid>
  )
}
